import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockTopics, mockCategories } from '../mockData';
import { 
  ArrowRight, BookOpen, Video, GraduationCap, Crown, Brain, CheckCircle, 
  Target, Users, Building2, Stethoscope, ClipboardCheck, Activity, Compass,
  ChevronRight, Play, Sparkles, Flame, Volume2, MessageCircle, Moon, Check,
  Award, Star, Bot, Heart, Lock, AlertCircle, FileText, Zap, Briefcase,
  Watch, BarChart3, Settings, Wind
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { useEducationAuth } from '../contexts/EducationAuthContext';
import { getTierLevel } from '../config/membershipConfig';
import { toast } from 'sonner';
import { EXECUTIVE_HEADSHOT_URL } from '../config/professionalIdentity';
import PromoBanner from '../components/PromoBanner';
import OnboardingModal from '../components/OnboardingModal';
import JourneySelector from '../components/JourneySelector';
import InstitutionalVideoPlayer from '../components/InstitutionalVideoPlayer';
import { useSiteContent, SitePreviewBadge } from '../hooks/useSiteContent';
// Sprint 29.1B — First live consumer of the SHS Visual Experience System.
// Wraps the hero grid as a single coherent unit so both columns enter together.
// Reduced-motion is honored automatically inside the primitive (§ 29.1A).
// Sprint 29.2A/B — Living Hero composition uses AirflowSignal + EEGSignal as
// ambient physiological layers. Governance: max two continuously animated
// physiological signals per hero unless explicitly authorized.
import { RiseIn, AirflowSignal, EEGSignal } from '../experience';

// ============================================
// BILINGUAL CONTENT FOR HOME PAGE
// ============================================
const getHomeContent = (language) => {
  const isEs = language === 'es';
  return {
    // Hero Section
    heroTitle: isEs 
      ? 'Tu Centro de Educación en Salud del Sueño' 
      : 'Your Sleep Health Education Hub',
    heroSubtitle: isEs
      ? 'Aprende, practica y certifícate con herramientas interactivas, IA y orientación experta.'
      : 'Learn, practice, and get certified with interactive tools, AI, and expert guidance.',
    startAssessment: isEs ? 'Iniciar Evaluación' : 'Start Assessment',
    exploreAcademy: isEs ? 'Explorar Academia' : 'Explore Academy',
    
    // AI Presenter
    meetDrEdgar: isEs ? 'Conoce a Dr. Edgar' : 'Meet Dr. Edgar',
    aiPresenterDesc: isEs 
      ? 'Tu guía personal de IA para educación en salud del sueño'
      : 'Your personal AI guide to sleep health education',
    askShsAi: isEs ? 'Pregunta a SHS-AI' : 'Ask SHS-AI',
    
    // Start Here Section
    startHereTitle: isEs ? 'Comienza Aquí' : 'Start Here',
    startHereSubtitle: isEs 
      ? 'Elige tu camino de aprendizaje'
      : 'Choose your learning path',
    
    // Entry Point 1 - Assessment
    assessmentTitle: isEs ? 'Evalúa Tu Sueño' : 'Assess Your Sleep',
    assessmentDesc: isEs
      ? 'Toma una evaluación completa del sueño para entender tus patrones y obtener recomendaciones personalizadas.'
      : 'Take a comprehensive sleep assessment to understand your patterns and get personalized recommendations.',
    startAssessmentBtn: isEs ? 'Comenzar Evaluación' : 'Start Assessment',
    
    // Entry Point 2 - Academy
    academyTitle: isEs ? 'Aprende y Mejora' : 'Learn & Improve',
    academyDesc: isEs
      ? 'Accede a módulos de aprendizaje interactivos, conferencias de audio y contenido experto sobre medicina del sueño.'
      : 'Access interactive learning modules, audio lectures, and expert content on sleep medicine.',
    exploreAcademyBtn: isEs ? 'Explorar Academia' : 'Explore Academy',
    
    // Entry Point 3 - Exam Preparation
    certificationTitle: isEs ? 'Preparación para Exámenes' : 'Exam Preparation',
    certificationDesc: isEs
      ? 'Practica con exámenes interactivos para CPSGT, RPSGT, PEDS y CCSH con modo de audio y retroalimentación inmediata.'
      : 'Practice with interactive exams for CPSGT, RPSGT, PEDS, and CCSH with audio mode and immediate feedback.',
    examPrepBtn: isEs ? 'Ir a Preparación' : 'Go to Exam Prep',
    
    // Educational boundary disclaimer (canonical footer text)
    boundaryDisclaimerTitle: isEs ? 'Solo educativo' : 'Educational only',
    boundaryDisclaimer: isEs
      ? 'Sleep Health Services proporciona información educativa y de bienestar únicamente. Este contenido no constituye consejo médico, diagnóstico ni tratamiento, y su uso no crea una relación médico-paciente. Para asuntos médicos, consulte a un profesional de la salud con licencia.'
      : 'Sleep Health Services provides educational and wellness information only. This content does not constitute medical advice, diagnosis, or treatment, and no physician-patient relationship is created by your use of this platform. For medical concerns, consult a licensed healthcare professional.',
    
    // Feature Groups
    learnTitle: isEs ? 'Aprender' : 'Learn',
    toolsTitle: isEs ? 'Herramientas' : 'Tools',
    professionalTitle: isEs ? 'Profesional' : 'Professional',
    premiumTitle: isEs ? 'Premium' : 'Premium',
    
    // LEARN Group
    topicsTitle: isEs ? 'Biblioteca de Temas' : 'Topic Library',
    topicsDesc: isEs ? '200+ artículos educativos' : '200+ educational articles',
    academyModulesTitle: isEs ? 'Módulos de Academia' : 'Academy Modules',
    academyModulesDesc: isEs ? 'Cursos de audio interactivos' : 'Interactive audio courses',
    techGuideTitle: isEs ? 'Guía de Bolsillo' : 'Tech Pocket Guide',
    techGuideDesc: isEs ? 'Referencia rápida para técnicos' : 'Quick reference for techs',
    
    // TOOLS Group
    sleepAssessmentTitle: isEs ? 'Evaluación del Sueño' : 'Sleep Assessment',
    sleepAssessmentDesc: isEs ? 'Cuestionarios validados' : 'Validated questionnaires',
    coachTitle: isEs ? 'Coach de Sueño' : 'Sleep Coach',
    coachDesc: isEs ? 'Seguimiento personalizado' : 'Personalized tracking',
    wearablesTitle: isEs ? 'Insights de Wearables' : 'Wearable Insights',
    wearablesDesc: isEs ? 'Analiza datos de dispositivos' : 'Analyze device data',
    
    // PROFESSIONAL Group
    examPrepTitle: isEs ? 'Exam Prep Pro' : 'Exam Prep Pro',
    examPrepDesc: isEs ? 'CPSGT, RPSGT, PEDS, CCSH' : 'CPSGT, RPSGT, PEDS, CCSH',
    scoringMasteryTitle: isEs ? 'Scoring Mastery' : 'Scoring Mastery',
    scoringMasteryDesc: isEs ? 'Entrenamiento en puntuación' : 'Scoring training',
    organizationsTitle: isEs ? 'Para Organizaciones' : 'For Organizations',
    organizationsDesc: isEs ? 'Soluciones empresariales' : 'Enterprise solutions',
    
    // PREMIUM Group
    membershipTitle: isEs ? 'Membresía' : 'Membership',
    membershipDesc: isEs ? 'Desbloquea todas las funciones' : 'Unlock all features',
    viewPlans: isEs ? 'Ver Planes' : 'View Plans',
    
    // Bottom CTA
    readyToStart: isEs ? '¿Listo para Empezar?' : 'Ready to Get Started?',
    readyToStartDesc: isEs 
      ? 'Únete a miles de profesionales del sueño que aprenden y crecen con Sleep Health Services.'
      : 'Join thousands of sleep professionals learning and growing with Sleep Health Services.',
    createFreeAccount: isEs ? 'Crear Cuenta Gratis' : 'Create Free Account',

    // Sleep Reset Academy Phase 1.5 — hero CTA + subtle link
    sraKicker: isEs ? 'Nuevo · Estructura de aprendizaje' : 'New · Structured learning',
    sraHeadline: isEs
      ? 'Sleep Reset Academy — Ruta Principiante ya disponible'
      : 'Sleep Reset Academy — Beginner Path now live',
    sraSubline: isEs
      ? '22 lecciones bilingües cortas. Alfabetización básica en salud del sueño, secuenciadas de principio a fin.'
      : '22 short bilingual lessons. Foundational sleep health literacy, sequenced end-to-end.',
    sraCta: isEs ? 'Entrar a la Academy' : 'Enter the Academy',
    sraLinkLabel: isEs ? 'Explorar Sleep Reset Academy →' : 'Explore Sleep Reset Academy →',
    
    // About section
    aboutTitle: isEs ? 'Acerca de Nosotros' : 'About Us',
    aboutDesc: isEs
      ? 'Sleep Health Services es una plataforma educativa líder fundada por el Dr. Edgar M. Paris Quiñones (Ph.D., educador en salud del sueño — no es médico MD/DO), dedicada a avanzar la educación en salud del sueño.'
      : 'Sleep Health Services is a leading educational platform founded by Dr. Edgar M. Paris Quiñones (Ph.D., sleep health educator — not a medical doctor MD/DO), dedicated to advancing sleep health education.',
    learnMore: isEs ? 'Saber Más' : 'Learn More',
    
    // Videos section
    videosTitle: isEs ? 'Videos Educativos' : 'Educational Videos',
    videosDesc: isEs
      ? 'Mira nuestro contenido de video gratuito sobre temas de salud del sueño.'
      : 'Watch our free video content on sleep health topics.',
    browseVideos: isEs ? 'Ver Videos' : 'Browse Videos',
    newVideosThisWeek: (n) => isEs
      ? (n === 1 ? '1 nuevo video esta semana' : `${n} nuevos videos esta semana`)
      : (n === 1 ? '1 new video this week' : `${n} new videos this week`),
    recentlyPublishedLabel: isEs ? 'Publicados recientemente' : 'Recently published',
  };
};

const HomePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const content = getHomeContent(language);
  const site = useSiteContent('home.hero');
  const lang2 = language === 'es' ? 'es' : 'en';
  const t = (key) => getTranslation(language, key);
  const { user, isAuthenticated } = useEducationAuth();

  // Recently-published bilingual video count + latest 3 topics (7-day window).
  // Uses existing /api/videos/recently-published endpoint over the existing
  // video_content collection — no new infrastructure.
  const [recentVideoCount, setRecentVideoCount] = useState(0);
  const [recentVideos, setRecentVideos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/recently-published?days=7&limit=3`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (typeof d.count === 'number') setRecentVideoCount(d.count);
        if (Array.isArray(d.recent)) setRecentVideos(d.recent);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Check if user has paid access
  const hasPaidAccess = useMemo(() => {
    if (!isAuthenticated || !user) return false;
    const effectiveTier = user.effective_tier || user.tier || 'free';
    return getTierLevel(effectiveTier) >= 1;
  }, [isAuthenticated, user]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="home-page">
      {/* Promo Banner for non-paid users */}
      {!hasPaidAccess && <PromoBanner />}
      
      {/* Onboarding Modal for new users */}
      {!isAuthenticated && <OnboardingModal />}
      
      {/* ============================================ */}
      {/* HERO SECTION WITH SHS AI PRESENTER */}
      {/* ============================================ */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>

        {/* Sprint 29.2A — Airflow ambient signal.
            Decorative, non-diagnostic. Never intercepts pointer events; never announced by
            screen readers. Positioned behind the hero container (z-10) so text/CTAs remain
            dominant. Opacity intentionally very low — "the interface is breathing", not
            "there is an animated graph behind the website".
            Sprint 29.2B — slightly nudged below vertical center so the EEG layer can occupy
            the upper spatial region. */}
        <AirflowSignal
          className="pointer-events-none select-none absolute inset-x-0 top-[62%] -translate-y-1/2 h-20 sm:h-24 md:h-32 text-sky-300 opacity-[0.10] md:opacity-[0.12]"
        />

        {/* Sprint 29.2B — EEG ambient signal.
            Second physiological layer. Placed in the upper spatial region of the hero so it
            brackets the content with Airflow (below) rather than stacking. Finer stroke,
            lower opacity, different animation period (11 s vs Airflow's 6 s → LCM 66 s beat,
            perceptually unsynchronized). Same decorative contract as Airflow. */}
        <EEGSignal
          className="pointer-events-none select-none absolute inset-x-0 top-[26%] -translate-y-1/2 h-10 sm:h-12 md:h-14 text-cyan-200 opacity-[0.08] md:opacity-[0.10]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <RiseIn className="grid md:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
            {/* Left Column - Text Content */}
            <div className="text-center md:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight tracking-tight">
                {site.text('title', lang2, content.heroTitle)}
              </h1>
              <p className="text-base sm:text-lg text-blue-100 mb-6 md:mb-8 max-w-xl mx-auto md:mx-0 leading-relaxed">
                {site.text('subtitle', lang2, content.heroSubtitle)}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
                <Button
                  size="lg"
                  onClick={() => navigate('/sleep-assessment')}
                  className="bg-white text-slate-900 hover:bg-blue-50 font-semibold px-6 py-5 text-base"
                  data-testid="hero-assessment-btn"
                >
                  <ClipboardCheck className="mr-2 h-5 w-5" />
                  {content.startAssessment}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/academy/sleep-reset')}
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-5 text-base"
                  data-testid="hero-academy-btn"
                >
                  <GraduationCap className="mr-2 h-5 w-5" />
                  {content.exploreAcademy}
                </Button>
              </div>

              <div className="mt-4 text-center md:text-left">
                <button
                  type="button"
                  onClick={() => navigate('/academy/sleep-reset')}
                  className="text-sm text-emerald-200 hover:text-emerald-100 underline underline-offset-4 decoration-emerald-400/40 hover:decoration-emerald-300 inline-flex items-center gap-1"
                  data-testid="hero-sleep-reset-academy-link"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {content.sraLinkLabel}
                </button>
              </div>

              {/* ITER182 · Public discovery CTAs — CPAP Mask Finder + Success Stories.
                  Docked below the primary hero so mobile users can reach them
                  in one tap without scrolling into deep marketing sections. */}
              <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start" data-testid="hero-discovery-ctas">
                <button
                  type="button"
                  onClick={() => navigate('/cpap-fit')}
                  className="text-sm text-white/95 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
                  data-testid="hero-cpap-mask-finder"
                >
                  <Wind className="h-3.5 w-3.5" aria-hidden="true" />
                  {language === 'es' ? 'Encuentra tu máscara CPAP' : 'Find your CPAP mask'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/cpap-stories')}
                  className="text-sm text-white/95 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
                  data-testid="hero-cpap-success-stories"
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  {language === 'es' ? 'Historias de éxito CPAP' : 'CPAP Success Stories'}
                </button>
              </div>
            </div>

            {/* Right Column - SHS AI Presenter Avatar */}
            <div className="flex justify-center md:justify-end">
              <div className="relative">
                {/* Avatar Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 max-w-xs">
                  <div className="flex flex-col items-center text-center">
                    {/* Avatar Image with Glow */}
                    <div className="relative mb-4">
                      <div className="absolute inset-0 bg-blue-400/30 rounded-full blur-xl animate-pulse"></div>
                      <img 
                        src={site.image('image', EXECUTIVE_HEADSHOT_URL)} 
                        alt="Dr. Edgar M. Paris Quiñones"
                        className="relative w-24 h-24 md:w-32 md:h-32 rounded-full object-cover object-top border-4 border-white/30 shadow-xl"
                        data-testid="hero-avatar-image"
                      />
                      <div className="absolute -bottom-1 -right-1 bg-green-500 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      </div>
                    </div>
                    
                    {/* Name & Title */}
                    <Badge className="bg-blue-500/80 text-white border-0 mb-2">SHS-AI</Badge>
                    <h3 className="font-semibold text-lg">{content.meetDrEdgar}</h3>
                    <p className="text-blue-200 text-sm mb-1">{content.aiPresenterDesc}</p>
                    <p className="text-blue-300/80 text-[10px] italic mb-3" data-testid="hero-credential-clarifier">
                      {language === 'es'
                        ? 'Ph.D. educador en salud del sueño · solo orientación educativa — no es médico'
                        : 'Ph.D. sleep health educator · educational guidance only — not a physician'}
                    </p>
                    
                    {/* Ask AI Button */}
                    <Button
                      onClick={() => navigate('/ai')}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="hero-ask-ai-btn"
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      {content.askShsAi}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </RiseIn>
        </div>
      </section>

      {/* ============================================ */}
      {/* SPRINT 29.2C — Intelligent Journey Selector  */}
      {/* First user-orientation layer. Additive; does */}
      {/* not replace Start Here or any other section. */}
      {/* ============================================ */}
      <SitePreviewBadge show={site.preview} />
      <InstitutionalVideoPlayer videoId="shs_v1_mission_vision" hideIfUnavailable sectionClassName="py-10 md:py-14 bg-slate-950 border-b border-slate-900" />

      <JourneySelector />

      {/* ============================================ */}
      {/* SLEEP RESET ACADEMY PHASE 1.5 — Featured CTA */}
      {/* ============================================ */}
      <section className="py-10 md:py-12 bg-slate-950 border-b border-slate-900" data-testid="sra-featured-cta">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="rounded-2xl overflow-hidden bg-gradient-to-r from-indigo-950 via-slate-900 to-emerald-950 border border-emerald-500/20 relative">
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_15%_50%,rgba(94,234,212,0.35),transparent_45%),radial-gradient(circle_at_85%_50%,rgba(129,140,248,0.35),transparent_45%)]" />
            <div className="relative p-6 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30 mb-3 backdrop-blur-sm">
                  <Sparkles className="h-3 w-3 mr-1" />
                  {content.sraKicker}
                </Badge>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-2">
                  {content.sraHeadline}
                </h2>
                <p className="text-white/70 max-w-xl leading-relaxed">
                  {content.sraSubline}
                </p>
              </div>
              <div className="flex md:justify-end">
                <Button
                  size="lg"
                  onClick={() => navigate('/academy/sleep-reset')}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold shadow-lg shadow-emerald-500/20"
                  data-testid="sra-hero-cta"
                >
                  <GraduationCap className="mr-2 h-5 w-5" />
                  {content.sraCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* Start Here retired in Sprint 29.2E — its    */}
      {/* three destinations remain reachable via:     */}
      {/*  • Sleep Assessment → Hero + Journey Selector*/}
      {/*  • Academy → Hero + Journey Selector + SRA + */}
      {/*    Features                                  */}
      {/*  • Certification Prep → Features PROFESSIONAL*/}
      {/* ============================================ */}

      {/* ============================================ */}
      {/* ORGANIZED FEATURES SECTION */}
      {/* ============================================ */}
      <InstitutionalVideoPlayer videoId="shs_v4_ecosystem" hideIfUnavailable sectionClassName="py-10 md:py-14 bg-slate-900" />

      <section className="py-12 md:py-16 bg-gray-50" data-testid="features-section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* LEARN Section */}
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <BookOpen className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">{content.learnTitle}</h3>
                <div className="flex-1 h-px bg-gray-200 ml-4"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard
                  icon={BookOpen}
                  title={content.topicsTitle}
                  description={content.topicsDesc}
                  color="blue"
                  onClick={() => navigate('/topics')}
                  testId="feature-topics"
                />
                <FeatureCard
                  icon={GraduationCap}
                  title={content.academyModulesTitle}
                  description={content.academyModulesDesc}
                  color="blue"
                  onClick={() => navigate('/academy/sleep-reset')}
                  testId="feature-academy"
                />
                <FeatureCard
                  icon={Compass}
                  title={content.techGuideTitle}
                  description={content.techGuideDesc}
                  color="blue"
                  onClick={() => navigate('/tech-pocket-guide')}
                  testId="feature-techguide"
                />
              </div>
            </div>

            {/* TOOLS Section */}
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Settings className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-bold text-gray-900">{content.toolsTitle}</h3>
                <div className="flex-1 h-px bg-gray-200 ml-4"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard
                  icon={ClipboardCheck}
                  title={content.sleepAssessmentTitle}
                  description={content.sleepAssessmentDesc}
                  color="emerald"
                  onClick={() => navigate('/sleep-assessment')}
                  testId="feature-assessment"
                />
                <FeatureCard
                  icon={Moon}
                  title={content.coachTitle}
                  description={content.coachDesc}
                  color="emerald"
                  onClick={() => navigate('/coach')}
                  testId="feature-coach"
                />
                <FeatureCard
                  icon={Watch}
                  title={content.wearablesTitle}
                  description={content.wearablesDesc}
                  color="emerald"
                  onClick={() => navigate('/wearable-insights')}
                  testId="feature-wearables"
                />
              </div>
            </div>

            {/* PROFESSIONAL Section */}
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Briefcase className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">{content.professionalTitle}</h3>
                <div className="flex-1 h-px bg-gray-200 ml-4"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard
                  icon={Target}
                  title={content.examPrepTitle}
                  description={content.examPrepDesc}
                  color="purple"
                  onClick={() => navigate('/exam/cpsgt')}
                  testId="feature-exams"
                />
                <FeatureCard
                  icon={BarChart3}
                  title={content.scoringMasteryTitle}
                  description={content.scoringMasteryDesc}
                  color="purple"
                  onClick={() => navigate('/scoring-mastery')}
                  testId="feature-scoring"
                />
                <FeatureCard
                  icon={Building2}
                  title={content.organizationsTitle}
                  description={content.organizationsDesc}
                  color="purple"
                  onClick={() => navigate('/organizations')}
                  testId="feature-organizations"
                />
              </div>
            </div>

            {/* PREMIUM Section */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Crown className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-bold text-gray-900">{content.premiumTitle}</h3>
                <div className="flex-1 h-px bg-gray-200 ml-4"></div>
              </div>
              <Card 
                className="border-2 border-amber-200 hover:border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50 cursor-pointer transition-all hover:shadow-lg"
                onClick={() => navigate('/membership')}
                data-testid="feature-membership"
              >
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Crown className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{content.membershipTitle}</h4>
                      <p className="text-sm text-gray-600">{content.membershipDesc}</p>
                    </div>
                  </div>
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    {content.viewPlans}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ABOUT SECTION (Sprint 29.2E — moved before   */}
      {/* Videos: trust/authority precedes freshness). */}
      {/* ============================================ */}
      <section className="py-12 bg-gray-50 border-b" data-testid="about-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
            <img 
              src={EXECUTIVE_HEADSHOT_URL} 
              alt="Dr. Edgar M. Paris Quiñones"
              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
            />
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{content.aboutTitle}</h2>
              <p className="text-gray-600 mb-4">{content.aboutDesc}</p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/about')}
                data-testid="learn-more-btn"
              >
                {content.learnMore}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* VIDEOS SECTION (Sprint 29.2E — repositioned  */}
      {/* after About; content freshness before        */}
      {/* regulatory boundary and conversion floor).   */}
      {/* ============================================ */}
      <section className="py-12 bg-white border-b" data-testid="videos-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Video className="h-7 w-7 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{content.videosTitle}</h2>
            {recentVideoCount > 0 && (
              <Badge
                variant="secondary"
                className="mb-3 bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
                data-testid="recently-published-badge"
              >
                <Sparkles className="h-3 w-3" />
                {content.newVideosThisWeek(recentVideoCount)}
              </Badge>
            )}
            {recentVideos.length > 0 && (
              <div className="mb-5" data-testid="recently-published-list">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  {content.recentlyPublishedLabel}
                </p>
                <ul className="flex flex-col items-center gap-1.5">
                  {recentVideos.map((v) => {
                    const title = (language === 'es' && v.title_es) ? v.title_es : v.title;
                    return (
                      <li key={v.topic_id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/topic/${v.topic_id}`)}
                          className="text-sm text-blue-700 hover:text-blue-900 hover:underline transition-colors inline-flex items-center gap-1.5"
                          data-testid={`recently-published-link-${v.topic_id}`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          {title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <p className="text-gray-600 mb-6">{content.videosDesc}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/videos')}
              data-testid="browse-videos-btn"
            >
              <Video className="mr-2 h-4 w-4" />
              {content.browseVideos}
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* EDUCATIONAL BOUNDARY DISCLAIMER (canonical footer) */}
      {/* ============================================ */}
      <section className="py-6 bg-amber-50 border-t border-amber-200" data-testid="home-educational-disclaimer">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">
                {content.boundaryDisclaimerTitle}
              </p>
              <p className="text-sm text-amber-800 leading-relaxed">
                {content.boundaryDisclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* BOTTOM CTA SECTION */}
      {/* ============================================ */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">{content.readyToStart}</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto text-base md:text-lg">
            {content.readyToStartDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-slate-900 hover:bg-blue-50 font-semibold px-8"
              onClick={() => navigate('/education/register')}
              data-testid="cta-register-btn"
            >
              {content.createFreeAccount}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 font-semibold px-8"
              onClick={() => navigate('/membership')}
              data-testid="cta-membership-btn"
            >
              <Crown className="mr-2 h-5 w-5" />
              {content.viewPlans}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================
// FEATURE CARD COMPONENT
// ============================================
const FeatureCard = ({ icon: Icon, title, description, color, onClick, testId }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 hover:border-blue-300',
    emerald: 'bg-emerald-100 text-emerald-600 hover:border-emerald-300',
    purple: 'bg-purple-100 text-purple-600 hover:border-purple-300',
    amber: 'bg-amber-100 text-amber-600 hover:border-amber-300',
  };

  return (
    <Card 
      className={`border hover:shadow-md transition-all cursor-pointer group ${colorClasses[color]?.split(' ').slice(2).join(' ') || 'hover:border-gray-300'}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses[color]?.split(' ').slice(0, 2).join(' ')}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
          <p className="text-xs text-gray-500 truncate">{description}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
      </CardContent>
    </Card>
  );
};

export default HomePage;
